import { API_URLS } from '@chessx/shared/api-routes'
import type {
  AdminPuzzleBulkResult,
  AdminPuzzleDto,
  AdminPuzzleInput,
  AdminPuzzleListQuery,
  AdminPuzzleListResponse
} from '@chessx/shared/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import axiosInstance from '../../network/axiosInstance'
import { toastMixin, toastErrorMixin } from '../../utils/interact'
import { chunkBySize } from './puzzleJson'

export const PUZZLES_QUERY_KEY = 'ADMIN_PUZZLES'

export const PUZZLES_PATH = '/admin/puzzles'

export const NEW_PUZZLE_ID = 'new'

const BULK_CHUNK_BYTES = 80_000
const EXPORT_PAGE_SIZE = 100

export const errorMessage = (error: unknown): string => {
  const response = (error as { response?: { data?: { error?: string } } })?.response
  return response?.data?.error ?? (error instanceof Error ? error.message : String(error))
}

const fetchPuzzlePage = async (query: AdminPuzzleListQuery): Promise<AdminPuzzleListResponse> => {
  const result = await axiosInstance.get(API_URLS.ADMIN_PUZZLES, { params: query })
  return result.data as AdminPuzzleListResponse
}

export const fetchAllPuzzles = async (query: AdminPuzzleListQuery): Promise<AdminPuzzleDto[]> => {
  const first = await fetchPuzzlePage({ ...query, page: 1, limit: EXPORT_PAGE_SIZE })
  const pageCount = Math.ceil(first.total / EXPORT_PAGE_SIZE)
  const rest = await Promise.all(
    Array.from({ length: Math.max(0, pageCount - 1) }, (_, index) =>
      fetchPuzzlePage({ ...query, page: index + 2, limit: EXPORT_PAGE_SIZE })
    )
  )
  return [first, ...rest].flatMap(page => page.puzzles)
}

export const usePuzzleList = (query: AdminPuzzleListQuery) =>
  useQuery({
    queryKey: [PUZZLES_QUERY_KEY, query],
    queryFn: () => fetchPuzzlePage(query),
    gcTime: 0
  })

export const usePuzzle = (id: string | undefined) =>
  useQuery({
    queryKey: [PUZZLES_QUERY_KEY, 'detail', id],
    queryFn: async (): Promise<AdminPuzzleDto> => {
      const result = await axiosInstance.get(`${API_URLS.ADMIN_PUZZLES}/${id}`)
      return (result.data as { puzzle: AdminPuzzleDto }).puzzle
    },
    enabled: Boolean(id) && id !== NEW_PUZZLE_ID,
    gcTime: 0
  })

const useInvalidatePuzzles = () => {
  const queryClient = useQueryClient()
  return () => queryClient.invalidateQueries({ queryKey: [PUZZLES_QUERY_KEY] })
}

export const useSavePuzzle = () => {
  const invalidate = useInvalidatePuzzles()

  return useMutation({
    mutationFn: async ({ id, input }: { id?: string; input: AdminPuzzleInput }) => {
      const result = id
        ? await axiosInstance.put(`${API_URLS.ADMIN_PUZZLES}/${id}`, input)
        : await axiosInstance.post(API_URLS.ADMIN_PUZZLES, input)
      return (result.data as { puzzle: AdminPuzzleDto }).puzzle
    },
    onSuccess: (_puzzle, { id }) => {
      toastMixin.fire({ title: id ? 'Puzzle updated' : 'Puzzle created' })
      invalidate()
    },
    onError: (error: unknown) => {
      toastErrorMixin.fire({ title: errorMessage(error) })
    }
  })
}

export const useDeletePuzzle = () => {
  const invalidate = useInvalidatePuzzles()

  return useMutation({
    mutationFn: async (id: string) => {
      await axiosInstance.delete(`${API_URLS.ADMIN_PUZZLES}/${id}`)
    },
    onSuccess: () => {
      toastMixin.fire({ title: 'Puzzle deleted' })
      invalidate()
    },
    onError: (error: unknown) => {
      toastErrorMixin.fire({ title: errorMessage(error) })
    }
  })
}

const offsetFailures = (result: AdminPuzzleBulkResult, offset: number): AdminPuzzleBulkResult => ({
  ...result,
  failed: result.failed.map(failure => ({ ...failure, index: failure.index + offset }))
})

const mergeResults = (
  total: AdminPuzzleBulkResult,
  next: AdminPuzzleBulkResult
): AdminPuzzleBulkResult => ({
  inserted: total.inserted + next.inserted,
  updated: total.updated + next.updated,
  failed: [...total.failed, ...next.failed]
})

export const useBulkUploadPuzzles = (onProgress: (done: number, total: number) => void) => {
  const invalidate = useInvalidatePuzzles()

  return useMutation({
    mutationFn: async (puzzles: unknown[]): Promise<AdminPuzzleBulkResult> => {
      const chunks = chunkBySize(puzzles, BULK_CHUNK_BYTES)
      let summary: AdminPuzzleBulkResult = { inserted: 0, updated: 0, failed: [] }
      let offset = 0
      onProgress(0, puzzles.length)
      for (const chunk of chunks) {
        const result = await axiosInstance.post(API_URLS.ADMIN_PUZZLES_BULK, { puzzles: chunk })
        summary = mergeResults(
          summary,
          offsetFailures(result.data as AdminPuzzleBulkResult, offset)
        )
        offset += chunk.length
        onProgress(offset, puzzles.length)
      }
      return summary
    },
    onSettled: () => invalidate(),
    onError: (error: unknown) => {
      toastErrorMixin.fire({ title: errorMessage(error) })
    }
  })
}
